import type { Centre } from '@/api/centresApi';
import { allCentreReviews } from '@/utils/centreStats';

export interface Therapist {
  name: string;
  qualification?: string;
  specialisation?: string;
  experience?: number;
  clinicName: string;
  clinicHref: string;
}

export interface Review {
  source?: 'recovry' | 'google';
  rating: number;
  comment: string;
  user?: string;
  clinicName: string;
  clinicHref: string;
}

export interface LocalProofData {
  therapists: Therapist[];
  reviews: Review[];
  priceFrom?: number;
  priceTo?: number;
  clinicCount: number;
}

/** Flatten what the clinics on this page actually carry. */
export function collectLocalProof(
  centres: Centre[],
  linkFor: (c: Centre) => string,
): LocalProofData {
  const therapists: Therapist[] = [];
  const reviews: Review[] = [];
  const prices: number[] = [];
  for (const c of centres) {
    const href = linkFor(c);
    for (const t of c.therapists ?? []) {
      if (t.name) therapists.push({ ...t, clinicName: c.name, clinicHref: href });
    }
    for (const s of c.services ?? []) {
      if (s.price && s.price > 0) prices.push(s.price);
    }
    for (const r of allCentreReviews(c)) {
      // Only reviews with a known origin: written on Recovry, or imported from Google and labelled.
      const known = r.verified === true || r.source === 'google';
      if (known && typeof r.rating === 'number' && r.comment?.trim()) {
        reviews.push({ rating: r.rating, comment: r.comment.trim(), user: r.user, source: r.source, clinicName: c.name, clinicHref: href });
      }
    }
  }
  // Best-rated first, so the section leads with its strongest proof.
  reviews.sort((a, b) => b.rating - a.rating);
  return {
    therapists,
    reviews,
    priceFrom: prices.length ? Math.min(...prices) : undefined,
    priceTo: prices.length ? Math.max(...prices) : undefined,
    clinicCount: centres.length,
  };
}
